import { Button, Form, Modal, Tabs, Typography } from 'antd';
import lodash from 'lodash';
import React from 'react';

import { WEEK_DAYS_MON_START } from '@/constants/date';

import type { VenueEditFormValues } from '../../../../types';
import { MODAL_BODY_STYLES } from '../../constants';
import { OpeningHourField } from './components/OpeningHourField';
import { validateTabSwitch } from './helpers';

const { useFormInstance } = Form;
const { Text } = Typography;

type EditOpeningHoursModalProps = {
  open: boolean;
  closeModal: () => void;
  currentTabKey: string;
  setCurrentTabKey: React.Dispatch<React.SetStateAction<string>>;
  initialOperatingHours: VenueEditFormValues['operatingHours'];
  updateOperatingHours: (
    newOperatingHours: VenueEditFormValues['operatingHours']
  ) => void;
};

export const EditOpeningHoursModal = ({
  open,
  closeModal,
  currentTabKey,
  setCurrentTabKey,
  initialOperatingHours,
  updateOperatingHours,
}: EditOpeningHoursModalProps) => {
  const form = useFormInstance<VenueEditFormValues>();

  const handleTabClick = async (tabKey: string) => {
    const prevDayIndex = Number(currentTabKey);
    const canSwitch = await validateTabSwitch(form, prevDayIndex);

    if (canSwitch) {
      setCurrentTabKey(tabKey);
    }
  };

  const saveOpeningHoursSetting = async () => {
    try {
      const operatingHours: VenueEditFormValues['operatingHours'] =
        await form.getFieldValue('operatingHours');

      const validateFieldKeys: (string | number)[][] = [];

      operatingHours.forEach(
        ({ day: dayIndex, times: allTimeRanges, status }) => {
          if (status !== 'open') {
            return;
          }

          const dayFieldKey = ['operatingHours', dayIndex, 'times'];

          if (allTimeRanges.length === 0) {
            validateFieldKeys.push(dayFieldKey);
            return;
          }

          allTimeRanges.forEach((_, timeRangeIndex) => {
            validateFieldKeys.push(
              dayFieldKey.concat([timeRangeIndex, 'startTime'])
            );
            validateFieldKeys.push(
              dayFieldKey.concat([timeRangeIndex, 'endTime'])
            );
          });
        }
      );

      await form.validateFields(validateFieldKeys);

      const sortedOperatingHours: VenueEditFormValues['operatingHours'] =
        lodash.cloneDeep(operatingHours);

      sortedOperatingHours.forEach((data) => {
        if (data.status === 'open') {
          data.times.sort(
            ({ startTime: aStartTime }, { startTime: bStartTime }) => {
              return aStartTime - bStartTime;
            }
          );
        }
      });

      updateOperatingHours(sortedOperatingHours);
    } catch (error) {
      // 驗證失敗，不執行任何事
    }
  };

  const tabsOfEditOpeningHours = WEEK_DAYS_MON_START.map((day, index) => ({
    label: `星期${day}`,
    key: index.toString(),
    children: (
      <OpeningHourField
        dayIndex={index}
        initialData={initialOperatingHours[index]}
      />
    ),
  }));

  return (
    <Modal
      centered
      destroyOnClose
      width={600}
      open={open}
      maskClosable={false}
      onCancel={closeModal}
      bodyStyle={MODAL_BODY_STYLES}
      footer={[
        <Button key="cancel" type="primary" onClick={closeModal}>
          取消
        </Button>,
        <Button key="ok" onClick={saveOpeningHoursSetting}>
          確定
        </Button>,
      ]}
    >
      <Text>設定開放時間</Text>
      <Tabs
        activeKey={currentTabKey}
        items={tabsOfEditOpeningHours}
        onTabClick={handleTabClick}
      />
    </Modal>
  );
};
